import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import api from "../lib/api";
import {
  Upload, FileText, Trash2, Sparkles, Download, X,
  Maximize2, ZoomIn, ZoomOut, FileUp, Folder, FolderOpen, ChevronRight, ChevronDown, Inbox,
  ShieldCheck, Wand2, Link2, Loader2, CheckCircle2
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import AssetVerificationModal from "../components/AssetVerificationModal";
import PageIntro from "../components/PageIntro";
import { toast } from "../components/ui/sonner";
import { useConfirm } from "../components/ConfirmDialog";

const DOC_TYPES = [
  { value: "contract_spa", label: "Contract / SPA" },
  { value: "title_deed", label: "Title Deed" },
  { value: "invoice", label: "Invoice" },
  { value: "tax_document", label: "Tax Document" },
  { value: "insurance", label: "Insurance" },
  { value: "passport", label: "Passport" },
  { value: "visa", label: "Visa" },
  { value: "payment_schedule", label: "Payment Schedule" },
  { value: "other", label: "Other" },
];
const typeLabel = (v) => DOC_TYPES.find(t => t.value === v)?.label || v || "Other";

const LOCATION_LABELS = { dubai: "Dubai", north_cyprus: "North Cyprus", germany: "Germany", other: "Somewhere Else" };

export default function DocumentVaultPage() {
  const [tree, setTree] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(null);
  const [extracting, setExtracting] = useState(null);
  const [extractingPlan, setExtractingPlan] = useState(null);
  const [verificationData, setVerificationData] = useState(null);
  const [verificationDocName, setVerificationDocName] = useState("");
  const [showVerification, setShowVerification] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewZoom, setPreviewZoom] = useState(100);
  const [dragOver, setDragOver] = useState(false);
  const [uploadFilename, setUploadFilename] = useState("");  // Visible during classify
  const [uploadSuccess, setUploadSuccess] = useState(null);  // {name, type, asset} for 3s confirmation
  const [expandedLocs, setExpandedLocs] = useState(() => new Set(["dubai", "north_cyprus", "germany", "other"]));
  const [selectedFolder, setSelectedFolder] = useState({ kind: "all" }); // {kind:"all"|"location"|"asset"|"unassigned", location?, asset_id?}
  const fileRef = useRef(null);
  const cameraRef = useRef(null);
  const confirm = useConfirm();

  const fetchTree = useCallback(async () => {
    try {
      const { data } = await api.get("/documents/tree");
      setTree(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTree(); }, [fetchTree]);

  // Cleanup preview blob on unmount
  useEffect(() => {
    return () => { if (previewUrl) URL.revokeObjectURL(previewUrl); };
  }, [previewUrl]);

  // Derive asset_id from selected folder to auto-link on upload
  const uploadPayloadAssetId = selectedFolder.kind === "asset" ? selectedFolder.asset_id : null;

  // Flat lookup so the post-upload success toast can name the linked asset.
  const assetIndex = useMemo(() => {
    const out = {};
    if (!tree) return out;
    (tree.locations || []).forEach(loc => {
      (loc.assets || []).forEach(a => { out[a.id] = { name: a.name, location: loc.location }; });
    });
    return out;
  }, [tree]);

  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    setUploadFilename(file.name || "document");
    setUploadSuccess(null);
    // Loud, visible toast — promises the user something is happening even if
    // they scroll the page away from the drop zone.
    const toastId = toast.loading(`Uploading ${file.name}…`, {
      description: "AI is classifying & linking your document.",
      duration: 60000,
    });
    try {
      const formData = new FormData();
      formData.append("file", file);
      const qs = new URLSearchParams({ auto_classify: "true" });
      if (uploadPayloadAssetId) qs.set("related_asset_id", uploadPayloadAssetId);
      const { data: doc } = await api.post(`/documents/upload?${qs.toString()}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await fetchTree();

      // Build a human-readable confirmation. typeLabel/asset lookup gives the
      // user concrete proof the classifier did its job.
      const typeTxt = typeLabel(doc.document_type);
      let linkedTxt = "";
      if (doc.related_asset_id) {
        const asset = assetIndex[doc.related_asset_id];
        if (asset) linkedTxt = ` · linked to ${asset.name}`;
      }
      toast.success(`${file.name} uploaded`, {
        id: toastId,
        description: `Classified as ${typeTxt}${linkedTxt}`,
        duration: 5000,
      });
      // Inline 3s success badge inside the drop zone for users who didn't
      // see the toast (e.g. mobile, or toast already dismissed).
      setUploadSuccess({ name: file.name, type: typeTxt, linked: linkedTxt });
      setTimeout(() => setUploadSuccess(null), 4000);
    } catch (err) {
      console.error("Upload error:", err);
      toast.error("Upload failed", {
        id: toastId,
        description: err.response?.data?.detail || "Please try again.",
      });
    } finally {
      setUploading(false);
      setUploadFilename("");
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) handleUpload(file);
  };

  const handleReassign = async (docId, payload) => {
    try {
      await api.put(`/documents/${docId}/assign`, payload);
      await fetchTree();
    } catch (err) {
      console.error("Reassign error:", err);
      toast.error("Could not re-assign document", { description: err.response?.data?.detail });
    }
  };

  const handleAnalyze = async (docId) => {
    setAnalyzing(docId);
    try {
      const { data } = await api.post(`/documents/${docId}/analyze`);
      await fetchTree();
      setSelectedDoc(prev => (prev && prev.id === docId ? { ...prev, ai_analysis: data.analysis } : prev));
    } catch (err) {
      console.error("Analysis error:", err);
      toast.error("Analysis failed", { description: err.response?.data?.detail });
    } finally {
      setAnalyzing(null);
    }
  };

  const handleSmartFill = async (doc) => {
    setExtracting(doc.id);
    try {
      const { data } = await api.post(`/documents/${doc.id}/extract-property`);
      if (data.extracted) {
        setVerificationData(data.extracted);
        setVerificationDocName(data.document_name);
        setShowVerification(true);
      }
    } catch (err) {
      console.error("Smart-fill error:", err);
      toast.error("Could not extract property data", { description: err.response?.data?.detail });
    } finally {
      setExtracting(null);
    }
  };

  const handleExtractPaymentPlan = async (doc) => {
    if (!doc.related_asset_id) {
      toast.warning("Link this document to an asset first", {
        description: "Use the dropdown below so the plan saves to the correct property.",
      });
      return;
    }
    setExtractingPlan(doc.id);
    try {
      const { data } = await api.post(`/documents/${doc.id}/extract-payment-plan`, {});
      toast.success(`Extracted ${data.extracted_count} milestones`, {
        description: `${Math.round((data.confidence || 0) * 100)}% confidence. Open the property to review.`,
      });
      await fetchTree();
    } catch (err) {
      toast.error("Could not extract payment plan", { description: err.response?.data?.detail });
    } finally {
      setExtractingPlan(null);
    }
  };

  const handleDelete = async (docId) => {
    try {
      await api.delete(`/documents/${docId}`);
      await fetchTree();
      if (selectedDoc?.id === docId) setSelectedDoc(null);
      if (previewDoc?.id === docId) closePreview();
    } catch (err) {
      console.error(err);
    }
  };

  // Bulk-delete every doc currently visible in the open folder. Confirms first.
  // Folders themselves are virtual (auto-generated from assets) so we delete
  // documents one-by-one and let the tree refresh redraw it.
  //
  // Special case: if the open folder is an EMPTY asset folder, the user is
  // really asking us to archive the property so the folder disappears. We
  // call DELETE /api/assets/{id} (archive) instead.
  const handleDeleteFolderDocs = async () => {
    const isAssetFolder = selectedFolder.kind === "asset";
    const isEmpty = visibleDocs.length === 0;

    // -- Empty asset folder → archive the underlying property -------------
    if (isAssetFolder && isEmpty) {
      const assetId = selectedFolder.asset_id;
      if (!assetId) return;
      const ok = await confirm({
        title: `Archive "${selectedFolderTitle}"?`,
        body: `This folder is auto-generated from the property "${selectedFolderTitle}".\n\nArchiving the property removes the folder. You can restore it later from the Archive in your Portfolio.`,
        confirmText: "Archive Property",
        cancelText: "Keep",
        tone: "danger",
      });
      if (!ok) return;
      try {
        await api.delete(`/assets/${assetId}`);
        setSelectedFolder({ kind: "all" });
        await fetchTree();
        toast.success(`Archived "${selectedFolderTitle}"`);
      } catch (err) {
        toast.error("Could not archive property", {
          description: err.response?.data?.detail || err.message,
        });
      }
      return;
    }

    // -- Folder has docs → bulk-delete every visible document -------------
    if (isEmpty) return;
    const count = visibleDocs.length;
    const ok = await confirm({
      title: `Delete ${count} document${count !== 1 ? "s" : ""}?`,
      body: `From folder "${selectedFolderTitle}". This cannot be undone.\n\nFolders are auto-generated from your portfolio — to remove the folder itself, archive the underlying property.`,
      confirmText: "Delete",
      cancelText: "Cancel",
      tone: "danger",
    });
    if (!ok) return;
    try {
      await Promise.all(
        visibleDocs.map((doc) => api.delete(`/documents/${doc.id}`))
      );
      await fetchTree();
      setSelectedDoc(null);
      if (previewDoc) closePreview();
      toast.success(`Deleted ${count} document${count !== 1 ? "s" : ""}`);
    } catch (err) {
      toast.error("Bulk delete failed", {
        description: err.response?.data?.detail || err.message,
      });
    }
  };

  const handleDownload = async (doc) => {
    try {
      const response = await api.get(`/documents/${doc.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(response.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.original_filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
    }
  };

  const handlePreview = async (doc) => {
    setPreviewLoading(true);
    setPreviewDoc(doc);
    setPreviewZoom(100);
    try {
      const response = await api.get(`/documents/${doc.id}/preview`, { responseType: "blob" });
      const blobUrl = URL.createObjectURL(response.data);
      setPreviewUrl(blobUrl);
    } catch (err) {
      console.error("Preview error:", err);
      toast.error("Failed to load preview");
      setPreviewDoc(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const closePreview = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewDoc(null);
    setPreviewUrl(null);
    setPreviewZoom(100);
  };

  const isImage = (ct) => ct?.startsWith("image/");
  const isPdf = (ct) => ct === "application/pdf";

  const formatSize = (bytes) => {
    if (!bytes) return "N/A";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const toggleLoc = (loc) => {
    setExpandedLocs(prev => {
      const next = new Set(prev);
      if (next.has(loc)) next.delete(loc); else next.add(loc);
      return next;
    });
  };

  // Flat list of ALL assets (for the reassign dropdown)
  const allAssets = useMemo(() => {
    if (!tree) return [];
    const out = [];
    tree.locations.forEach(l => {
      l.assets.forEach(a => out.push({ ...a, location: l.location }));
    });
    return out;
  }, [tree]);

  // Active documents based on selected folder
  const visibleDocs = useMemo(() => {
    if (!tree) return [];
    const sel = selectedFolder;
    const all = [];
    tree.locations.forEach(l => {
      l.assets.forEach(a => all.push(...a.documents));
      all.push(...l.unassigned);
    });
    all.push(...(tree.unassigned || []));
    if (sel.kind === "all") return all;
    if (sel.kind === "unassigned") {
      const list = [];
      tree.locations.forEach(l => list.push(...l.unassigned));
      list.push(...(tree.unassigned || []));
      return list;
    }
    if (sel.kind === "location") {
      const loc = tree.locations.find(l => l.location === sel.location);
      if (!loc) return [];
      const list = [...loc.unassigned];
      loc.assets.forEach(a => list.push(...a.documents));
      return list;
    }
    if (sel.kind === "asset") {
      for (const l of tree.locations) {
        const a = l.assets.find(x => x.id === sel.asset_id);
        if (a) return a.documents;
      }
    }
    return [];
  }, [tree, selectedFolder]);

  useEffect(() => {
    if (!previewDoc) return;
    const handleEsc = (e) => { if (e.key === "Escape") closePreview(); };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const selectedFolderTitle = (() => {
    if (!tree) return "Documents";
    if (selectedFolder.kind === "all") return "All Documents";
    if (selectedFolder.kind === "unassigned") return "Unassigned";
    if (selectedFolder.kind === "location") return LOCATION_LABELS[selectedFolder.location] || selectedFolder.location;
    if (selectedFolder.kind === "asset") {
      const a = allAssets.find(x => x.id === selectedFolder.asset_id);
      return a ? a.name : "Asset";
    }
    return "Documents";
  })();

  const totalDocsCount = (tree?.locations || []).reduce((acc, l) =>
    acc + l.unassigned.length + l.assets.reduce((s, a) => s + a.documents.length, 0), 0
  ) + (tree?.unassigned?.length || 0);

  return (
    <div data-testid="document-vault-page">
      <PageIntro
        pageKey="vault"
        title="Your secure document vault"
        subtitle="SPAs, title deeds, passports, visas — all encrypted at rest in our EU vault."
        bullets={[
          { icon: ShieldCheck, label: "End-to-end encryption", text: "Every file is Fernet-encrypted before it reaches Cloudflare R2. Even we can't read it without your session." },
          { icon: Wand2, label: "AI auto-tag + extract", text: "Upload an SPA and we OCR it, classify the type, and link it to the matching asset automatically." },
          { icon: Link2, label: "Payment plan AI", text: "Extract the full payment schedule from any off-plan SPA in one click — milestones land on the asset." },
        ]}
      />
      <div className="mb-6 md:mb-8">
        <p className="text-[11px] tracking-[0.15em] uppercase text-white/60 font-sans mb-1">Secure Vault</p>
        <h1 className="font-serif text-3xl sm:text-4xl text-white tracking-tight">Document Vault</h1>
        <p className="text-white/60 text-xs sm:text-sm mt-1">Organised by location & property. AI auto-tags and links your uploads.</p>
      </div>

      <div className="grid grid-cols-12 gap-4 md:gap-6">
        {/* ================== LEFT: FOLDER TREE ================== */}
        <aside className="col-span-12 lg:col-span-3 space-y-3" data-testid="vault-sidebar">
          {/* Upload zone */}
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
            className="bg-[#0A0A0A] border border-white/[0.10] p-4">
            <p className="text-[10px] tracking-[0.15em] uppercase text-white/60 mb-3">Upload</p>
            {selectedFolder.kind === "asset" && (
              <p className="text-[10px] text-[#D4AF37]/70 mb-2" data-testid="upload-target-hint">
                Will link to: {selectedFolderTitle}
              </p>
            )}
            <div
              onDragOver={e => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              onClick={() => !uploading && fileRef.current?.click()}
              data-testid="doc-upload-zone"
              aria-busy={uploading}
              aria-live="polite"
              className={`border-2 border-dashed py-6 px-3 text-center transition-all ${
                uploading
                  ? "border-[#D4AF37] bg-[#D4AF37]/[0.08] cursor-wait"
                  : uploadSuccess
                    ? "border-emerald-400/60 bg-emerald-400/[0.06] cursor-pointer"
                    : dragOver
                      ? "border-[#D4AF37] bg-[#D4AF37]/5 cursor-pointer"
                      : "border-white/10 hover:border-white/20 cursor-pointer"
              }`}
            >
              {uploading ? (
                <>
                  <Loader2 className="w-7 h-7 mx-auto mb-2 text-[#D4AF37] animate-spin" strokeWidth={1.5} />
                  <p className="text-[#D4AF37] text-sm font-medium mb-1" data-testid="doc-upload-status">
                    Classifying with AI…
                  </p>
                  <p className="text-white/70 text-[10px] truncate" title={uploadFilename}>
                    {uploadFilename}
                  </p>
                  <p className="text-white/45 text-[9px] mt-1">This usually takes 3-10 seconds</p>
                </>
              ) : uploadSuccess ? (
                <>
                  <CheckCircle2 className="w-7 h-7 mx-auto mb-2 text-emerald-400" strokeWidth={1.5} />
                  <p className="text-emerald-300 text-sm font-medium mb-1" data-testid="doc-upload-success">
                    Uploaded
                  </p>
                  <p className="text-white/70 text-[10px] leading-snug">
                    {uploadSuccess.type}{uploadSuccess.linked}
                  </p>
                </>
              ) : (
                <>
                  <Upload className={`w-6 h-6 mx-auto mb-2 ${dragOver ? "text-[#D4AF37]" : "text-white/55"}`} strokeWidth={1} />
                  <p className="text-white/60 text-xs mb-0.5">Drop or click</p>
                  <p className="text-white/55 text-[9px]">AI auto-tags &amp; links</p>
                </>
              )}
            </div>
            {/* Hidden file inputs:
                • fileRef = generic file picker (used by drop zone & desktop click)
                • cameraRef = `capture="environment"` opens phone's back camera directly,
                  great for snapping an SPA on the go without saving to camera roll first. */}
            <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp"
              className="hidden" onChange={e => handleUpload(e.target.files?.[0])}
              data-testid="doc-file-input" />
            <input
              ref={cameraRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={e => handleUpload(e.target.files?.[0])}
              data-testid="doc-camera-input"
            />
            {/* On phones (md:hidden), surface a dedicated "Take photo" button — */}
            {/* invisible on desktop, where the drop zone already handles the case. */}
            <button
              type="button"
              onClick={() => cameraRef.current?.click()}
              data-testid="doc-camera-btn"
              className="md:hidden mt-3 w-full border border-white/10 hover:border-[#D4AF37]/40 text-white/85 hover:text-[#D4AF37] text-xs py-2.5 transition-colors flex items-center justify-center gap-2 min-h-[44px]"
            >
              <FileUp className="w-3.5 h-3.5" />
              Take photo with camera
            </button>
          </motion.div>

          {/* Folder tree */}
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
            className="bg-[#0A0A0A] border border-white/[0.10] p-3">
            <p className="text-[10px] tracking-[0.15em] uppercase text-white/60 mb-2 px-2">Folders</p>

            {/* All */}
            <FolderRow
              testid="folder-all"
              label="All Documents"
              count={totalDocsCount}
              active={selectedFolder.kind === "all"}
              onClick={() => setSelectedFolder({ kind: "all" })}
              icon={<Folder className="w-3.5 h-3.5" strokeWidth={1.5} />}
            />

            {/* Locations */}
            {tree?.locations.map(loc => {
              const expanded = expandedLocs.has(loc.location);
              const locCount = loc.unassigned.length + loc.assets.reduce((s, a) => s + a.documents.length, 0);
              return (
                <div key={loc.location}>
                  <div className="flex items-center">
                    <button
                      className="p-1 text-white/65 hover:text-white"
                      onClick={() => toggleLoc(loc.location)}
                      data-testid={`folder-toggle-${loc.location}`}
                      aria-label={`toggle ${loc.location}`}
                    >
                      {expanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                    </button>
                    <FolderRow
                      testid={`folder-${loc.location}`}
                      label={LOCATION_LABELS[loc.location] || loc.location}
                      count={locCount}
                      active={selectedFolder.kind === "location" && selectedFolder.location === loc.location}
                      onClick={() => setSelectedFolder({ kind: "location", location: loc.location })}
                      icon={expanded ? <FolderOpen className="w-3.5 h-3.5 text-[#D4AF37]/70" strokeWidth={1.5} /> : <Folder className="w-3.5 h-3.5 text-[#D4AF37]/70" strokeWidth={1.5} />}
                      flex
                    />
                  </div>
                  {expanded && (
                    <div className="pl-5 space-y-0.5">
                      {loc.assets.length === 0 && (
                        <p className="text-white/55 text-[10px] px-3 py-1.5 italic">No properties yet</p>
                      )}
                      {loc.assets.map(a => (
                        <FolderRow
                          key={a.id}
                          testid={`folder-asset-${a.id}`}
                          label={a.name}
                          count={a.documents.length}
                          active={selectedFolder.kind === "asset" && selectedFolder.asset_id === a.id}
                          onClick={() => setSelectedFolder({ kind: "asset", asset_id: a.id })}
                          icon={<FileText className="w-3 h-3 text-white/60" strokeWidth={1.5} />}
                          small
                        />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}

            {/* Unassigned */}
            <FolderRow
              testid="folder-unassigned"
              label="Unassigned"
              count={(tree?.locations || []).reduce((s, l) => s + l.unassigned.length, 0) + (tree?.unassigned?.length || 0)}
              active={selectedFolder.kind === "unassigned"}
              onClick={() => setSelectedFolder({ kind: "unassigned" })}
              icon={<Inbox className="w-3.5 h-3.5 text-white/60" strokeWidth={1.5} />}
            />
          </motion.div>
        </aside>

        {/* ================== RIGHT: DOCUMENT LIST ================== */}
        <section className="col-span-12 lg:col-span-9" data-testid="vault-content">
          <div className="flex items-baseline justify-between mb-4">
            <div>
              <p className="text-[10px] tracking-[0.15em] uppercase text-white/65 mb-0.5">
                {selectedFolder.kind === "asset" ? "Property" : selectedFolder.kind === "location" ? "Location" : "Folder"}
              </p>
              <h2 className="font-serif text-2xl text-white" data-testid="folder-title">{selectedFolderTitle}</h2>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-white/65 text-xs font-mono">{visibleDocs.length} file{visibleDocs.length !== 1 ? "s" : ""}</span>
              {(visibleDocs.length > 0 || selectedFolder.kind === "asset") && (
                <button
                  type="button"
                  onClick={handleDeleteFolderDocs}
                  data-testid="folder-bulk-delete"
                  title={
                    visibleDocs.length === 0 && selectedFolder.kind === "asset"
                      ? `Archive property "${selectedFolderTitle}" to remove this folder`
                      : `Delete all ${visibleDocs.length} documents in this folder`
                  }
                  className="text-white/65 hover:text-red-400 transition-colors p-1.5 -m-1.5"
                  aria-label={
                    visibleDocs.length === 0 && selectedFolder.kind === "asset"
                      ? "Archive property to remove folder"
                      : "Delete all documents in this folder"
                  }
                >
                  <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                </button>
              )}
            </div>
          </div>

          <div className="space-y-3">
            {visibleDocs.length > 0 ? visibleDocs.map(doc => (
              <div key={doc.id} className="bg-[#0A0A0A] border border-white/[0.10] p-4 hover:border-white/[0.1] transition-colors"
                data-testid={`doc-item-${doc.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap sm:flex-nowrap">
                  <div className="flex items-start gap-3 flex-1 min-w-0 cursor-pointer w-full sm:w-auto"
                    onClick={() => handlePreview(doc)} data-testid={`preview-doc-${doc.id}`}>
                    <div className="w-10 h-10 bg-[#D4AF37]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <FileText className="w-4 h-4 text-[#D4AF37]" strokeWidth={1.5} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-white text-sm truncate hover:text-[#D4AF37] transition-colors">{doc.original_filename}</p>
                      <div className="flex items-center gap-2 sm:gap-3 mt-1 flex-wrap">
                        <span className="text-[9px] tracking-wider uppercase bg-[#D4AF37]/10 text-[#D4AF37]/80 px-1.5 py-0.5"
                          data-testid={`doc-type-badge-${doc.id}`}>
                          {typeLabel(doc.document_type)}
                        </span>
                        {doc.location && (
                          <span className="text-white/65 text-[10px]">{LOCATION_LABELS[doc.location] || doc.location}</span>
                        )}
                        <span className="text-white/55 text-[10px]">{formatSize(doc.size)}</span>
                        <span className="text-white/55 text-[10px] hidden sm:inline">{new Date(doc.created_at).toLocaleDateString()}</span>
                        {doc.ai_classification?.confidence > 0 && (
                          <span className="text-[9px] text-white/65" title="AI classification confidence">
                            AI {Math.round(doc.ai_classification.confidence * 100)}%
                          </span>
                        )}
                      </div>
                      {/* Reassign controls */}
                      <div className="flex items-center gap-2 mt-2 flex-wrap">
                        <select
                          data-testid={`reassign-asset-${doc.id}`}
                          value={doc.related_asset_id || ""}
                          onChange={e => handleReassign(doc.id, { related_asset_id: e.target.value || null })}
                          onClick={e => e.stopPropagation()}
                          className="bg-[#050505] border border-white/10 text-white/90 text-[11px] sm:text-[10px] px-2 py-1.5 sm:py-1 focus:border-[#D4AF37] outline-none max-w-full"
                        >
                          <option value="">— Not linked —</option>
                          {allAssets.map(a => (
                            <option key={a.id} value={a.id}>
                              {LOCATION_LABELS[a.location] || a.location} · {a.name}
                            </option>
                          ))}
                        </select>
                        <select
                          data-testid={`reassign-type-${doc.id}`}
                          value={doc.document_type || "other"}
                          onChange={e => handleReassign(doc.id, { document_type: e.target.value })}
                          onClick={e => e.stopPropagation()}
                          className="bg-[#050505] border border-white/10 text-white/90 text-[11px] sm:text-[10px] px-2 py-1.5 sm:py-1 focus:border-[#D4AF37] outline-none"
                        >
                          {DOC_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                        </select>
                      </div>
                      {doc.ai_analysis && (
                        <div className="mt-2 bg-[#D4AF37]/5 border border-[#D4AF37]/10 px-3 py-2">
                          <p className="text-[#D4AF37] text-[10px] uppercase tracking-wider mb-1">AI Analysis Available</p>
                          <p className="text-white/85 text-xs line-clamp-2">{doc.ai_analysis?.summary || "Click to view full analysis"}</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Action buttons — on phones, wrap to a full-width row below the file info. */}
                  <div className="flex items-center gap-1 sm:gap-1.5 flex-shrink-0 flex-wrap w-full sm:w-auto justify-end pt-2 sm:pt-0 border-t sm:border-0 border-white/[0.08]">
                    {/* Two AI extraction buttons only render for document
                        types where they make sense. Both auto-classification
                        AND manual dropdown selection flow through the same
                        `doc.document_type` field, so this single check covers
                        both paths. An invoice / passport / title deed doesn't
                        get either button (was: unconditional).
                          • Create Asset from Contract → contract_spa only
                          • Extract Payment Plan       → contract_spa OR payment_schedule
                    */}
                    {doc.document_type === "contract_spa" && (
                      <button
                        onClick={() => handleSmartFill(doc)}
                        disabled={extracting === doc.id}
                        data-testid={`smart-fill-${doc.id}`}
                        className="transition-colors disabled:animate-pulse"
                        style={{
                          background: "rgba(212,175,55,0.1)",
                          border: "0.5px solid rgba(212,175,55,0.35)",
                          color: "#D4AF37",
                          borderRadius: "5px",
                          padding: "4px 9px",
                          fontSize: "10px",
                          textTransform: "uppercase",
                          letterSpacing: "0.06em",
                        }}
                        title="Create Asset from Contract"
                      >
                        Create Asset from Contract
                      </button>
                    )}
                    {(doc.document_type === "contract_spa" ||
                      doc.document_type === "payment_schedule") && (
                      <button
                        onClick={() => handleExtractPaymentPlan(doc)}
                        disabled={extractingPlan === doc.id}
                        data-testid={`extract-plan-${doc.id}`}
                        className="transition-colors disabled:animate-pulse"
                        style={{
                          background: "transparent",
                          border: "0.5px solid #2a2a2a",
                          color: "#888",
                          borderRadius: "5px",
                          padding: "4px 9px",
                          fontSize: "10px",
                          textTransform: "uppercase",
                          letterSpacing: "0.06em",
                        }}
                        title="Extract Payment Plan (requires linked asset)"
                      >
                        Extract Payment Plan
                      </button>
                    )}
                    {/* AI Analyze — text button. Once an analysis exists,
                        the button flips to "View Analysis" in-place (same
                        slot, same testid family) so the affordance stays
                        exactly where the user last looked. */}
                    {doc.ai_analysis ? (
                      <button
                        onClick={() => setSelectedDoc(doc)}
                        data-testid={`view-analysis-${doc.id}`}
                        className="transition-colors"
                        style={{
                          background: "rgba(212,175,55,0.1)",
                          border: "0.5px solid rgba(212,175,55,0.35)",
                          color: "#D4AF37",
                          borderRadius: "5px",
                          padding: "4px 9px",
                          fontSize: "10px",
                          textTransform: "uppercase",
                          letterSpacing: "0.06em",
                        }}
                        title="View AI Analysis"
                      >
                        View Analysis
                      </button>
                    ) : (
                      <button
                        onClick={() => handleAnalyze(doc.id)}
                        disabled={analyzing === doc.id}
                        data-testid={`analyze-doc-${doc.id}`}
                        className="transition-colors disabled:animate-pulse"
                        style={{
                          background: "transparent",
                          border: "0.5px solid #2a2a2a",
                          color: "#888",
                          borderRadius: "5px",
                          padding: "4px 9px",
                          fontSize: "10px",
                          textTransform: "uppercase",
                          letterSpacing: "0.06em",
                        }}
                        title="AI Analyze"
                      >
                        {analyzing === doc.id ? "Analyzing…" : "AI Analyze"}
                      </button>
                    )}
                    <button onClick={() => handlePreview(doc)} data-testid={`preview-btn-${doc.id}`}
                      className="p-2.5 sm:p-2 text-white/65 hover:text-[#D4AF37] transition-colors min-w-[40px] min-h-[40px] flex items-center justify-center" title="Preview">
                      <Maximize2 className="w-4 h-4" />
                    </button>
                    <button onClick={() => handleDownload(doc)} data-testid={`download-doc-${doc.id}`}
                      className="p-2.5 sm:p-2 text-white/65 hover:text-white transition-colors min-w-[40px] min-h-[40px] flex items-center justify-center" title="Download">
                      <Download className="w-4 h-4" />
                    </button>
                    <button onClick={() => handleDelete(doc.id)} data-testid={`delete-doc-${doc.id}`}
                      className="p-2.5 sm:p-2 text-white/65 hover:text-red-400 transition-colors min-w-[40px] min-h-[40px] flex items-center justify-center" title="Delete">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            )) : (
              <div className="bg-[#0A0A0A] border border-white/[0.10] p-12 text-center">
                <FileText className="w-12 h-12 text-white/10 mx-auto mb-3" strokeWidth={1} />
                <p className="text-white/65 text-sm mb-1">No documents here</p>
                <p className="text-white/55 text-xs">
                  {selectedFolder.kind === "asset"
                    ? "Upload a document while this folder is selected to auto-link it."
                    : "Upload SPAs, Title Deeds, or Invoices. AI will tag &amp; link them."}
                </p>
              </div>
            )}
          </div>
        </section>
      </div>

      {/* ===== AI Analysis Modal ===== */}
      <AnimatePresence>
        {selectedDoc && !previewDoc && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/80 z-50" onClick={() => setSelectedDoc(null)} />
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
              className="fixed inset-3 md:inset-6 z-50 bg-[#0A0A0A] border border-white/[0.12] overflow-auto flex flex-col"
              data-testid="ai-analysis-modal">
              <div className="flex items-center justify-between px-6 py-4 border-b border-white/[0.10] sticky top-0 bg-[#0A0A0A] z-10">
                <div className="flex items-center gap-3 min-w-0">
                  <Sparkles className="w-4 h-4 text-[#D4AF37] flex-shrink-0" strokeWidth={1.5} />
                  <div className="min-w-0">
                    <p className="text-[10px] tracking-[0.15em] uppercase text-white/65 font-sans">AI Analysis</p>
                    <h3 className="font-serif text-lg text-white truncate">{selectedDoc.original_filename || "Document"}</h3>
                  </div>
                </div>
                <button onClick={() => setSelectedDoc(null)} className="text-white/60 hover:text-white p-2" data-testid="close-analysis-modal">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="px-6 py-6 space-y-6">
                {(() => {
                  const a = selectedDoc.ai_analysis || {};
                  const sections = [];

                  if (a.document_type || a.confidence != null) {
                    sections.push(
                      <div key="meta" className="flex items-center gap-3 flex-wrap">
                        {a.document_type && (
                          <span className="text-[10px] tracking-[0.15em] uppercase bg-[#D4AF37]/10 text-[#D4AF37] border border-[#D4AF37]/20 px-2.5 py-1">
                            {String(a.document_type).replace(/_/g, " ")}
                          </span>
                        )}
                        {a.confidence != null && (
                          <span className="text-[10px] tracking-wider uppercase text-white/60">
                            Confidence: {Math.round((a.confidence || 0) * 100)}%
                          </span>
                        )}
                      </div>
                    );
                  }

                  if (a.summary) {
                    sections.push(
                      <section key="summary">
                        <p className="text-[10px] tracking-[0.15em] uppercase text-white/65 mb-2">Summary</p>
                        <p className="text-white/80 text-sm leading-relaxed">{a.summary}</p>
                      </section>
                    );
                  }

                  if (Array.isArray(a.key_dates) && a.key_dates.length > 0) {
                    sections.push(
                      <section key="dates">
                        <p className="text-[10px] tracking-[0.15em] uppercase text-white/65 mb-2">Key Dates</p>
                        <ul className="space-y-2">
                          {a.key_dates.map((d, idx) => (
                            <li key={`${d.date || idx}-${d.description || ""}`} className="flex items-start gap-3 border-l-2 border-[#D4AF37]/30 pl-3 py-1">
                              <span className="text-[#D4AF37] text-xs font-mono whitespace-nowrap">{d.date || "—"}</span>
                              <span className="text-white/90 text-xs flex-1">{d.description || "—"}</span>
                            </li>
                          ))}
                        </ul>
                      </section>
                    );
                  }

                  if (Array.isArray(a.financial_figures) && a.financial_figures.length > 0) {
                    sections.push(
                      <section key="financials">
                        <p className="text-[10px] tracking-[0.15em] uppercase text-white/65 mb-2">Financial Figures</p>
                        <ul className="space-y-1.5">
                          {a.financial_figures.map((f, idx) => (
                            <li key={`${f.amount || idx}-${f.description || ""}`} className="flex items-baseline gap-3 text-xs">
                              <span className="text-white font-mono whitespace-nowrap">
                                {f.currency || ""} {typeof f.amount === "number" ? f.amount.toLocaleString() : (f.amount || "")}
                              </span>
                              <span className="text-white/65">·</span>
                              <span className="text-white/90">{f.description || "—"}</span>
                            </li>
                          ))}
                        </ul>
                      </section>
                    );
                  }

                  if (Array.isArray(a.parties) && a.parties.length > 0) {
                    sections.push(
                      <section key="parties">
                        <p className="text-[10px] tracking-[0.15em] uppercase text-white/65 mb-2">Parties</p>
                        <ul className="space-y-1.5">
                          {a.parties.map((p, idx) => (
                            <li key={`${p.name || idx}-${p.role || ""}`} className="flex items-baseline gap-3 text-xs">
                              <span className="text-white">{p.name || "—"}</span>
                              <span className="text-white/60 text-[10px] uppercase tracking-wider">{p.role || ""}</span>
                            </li>
                          ))}
                        </ul>
                      </section>
                    );
                  }

                  const pd = a.property_details;
                  if (pd && (pd.location || pd.size || pd.unit)) {
                    sections.push(
                      <section key="prop">
                        <p className="text-[10px] tracking-[0.15em] uppercase text-white/65 mb-2">Property Details</p>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs">
                          {pd.location && (
                            <div className="bg-white/[0.02] border border-white/[0.08] px-3 py-2">
                              <p className="text-white/65 text-[10px] uppercase tracking-wider mb-1">Location</p>
                              <p className="text-white/80">{pd.location}</p>
                            </div>
                          )}
                          {pd.unit && (
                            <div className="bg-white/[0.02] border border-white/[0.08] px-3 py-2">
                              <p className="text-white/65 text-[10px] uppercase tracking-wider mb-1">Unit</p>
                              <p className="text-white/80">{pd.unit}</p>
                            </div>
                          )}
                          {pd.size && (
                            <div className="bg-white/[0.02] border border-white/[0.08] px-3 py-2">
                              <p className="text-white/65 text-[10px] uppercase tracking-wider mb-1">Size</p>
                              <p className="text-white/80">{pd.size}</p>
                            </div>
                          )}
                        </div>
                      </section>
                    );
                  }

                  if (Array.isArray(a.compliance_notes) && a.compliance_notes.length > 0) {
                    sections.push(
                      <section key="compliance">
                        <p className="text-[10px] tracking-[0.15em] uppercase text-white/65 mb-2">Compliance Notes</p>
                        <ul className="space-y-1.5">
                          {a.compliance_notes.map((c, idx) => (
                            <li key={`${idx}-${typeof c === "string" ? c.slice(0, 24) : ""}`} className="flex items-start gap-2 text-xs text-white/90">
                              <span className="text-[#D4AF37] mt-0.5">·</span>
                              <span>{c}</span>
                            </li>
                          ))}
                        </ul>
                      </section>
                    );
                  }

                  if (sections.length === 0) {
                    return (
                      <p className="text-white/65 text-sm py-8 text-center">
                        No structured insights could be extracted from this document.
                      </p>
                    );
                  }

                  return sections;
                })()}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* ===== Document Preview Modal ===== */}
      <AnimatePresence>
        {previewDoc && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/80 z-50" onClick={closePreview} />
            <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.97 }}
              className="fixed inset-3 md:inset-6 z-50 flex flex-col bg-[#0A0A0A] border border-white/[0.12] overflow-hidden"
              data-testid="preview-modal">
              <div className="flex items-center justify-between px-5 py-3 border-b border-white/[0.10] bg-[#050505] flex-shrink-0">
                <div className="flex items-center gap-3 min-w-0">
                  <FileText className="w-4 h-4 text-[#D4AF37] flex-shrink-0" strokeWidth={1.5} />
                  <div className="min-w-0">
                    <p className="text-white text-sm truncate">{previewDoc.original_filename}</p>
                    <p className="text-white/65 text-[10px]">
                      {typeLabel(previewDoc.document_type)} &middot; {formatSize(previewDoc.size)}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {isImage(previewDoc.content_type) && previewUrl && (
                    <div className="flex items-center gap-1 mr-2 border border-white/[0.10] px-1">
                      <button onClick={() => setPreviewZoom(z => Math.max(25, z - 25))}
                        data-testid="preview-zoom-out"
                        className="p-1.5 text-white/60 hover:text-white transition-colors" title="Zoom out">
                        <ZoomOut className="w-3.5 h-3.5" />
                      </button>
                      <span className="text-white/65 text-[10px] font-mono w-10 text-center">{previewZoom}%</span>
                      <button onClick={() => setPreviewZoom(z => Math.min(300, z + 25))}
                        data-testid="preview-zoom-in"
                        className="p-1.5 text-white/60 hover:text-white transition-colors" title="Zoom in">
                        <ZoomIn className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  )}
                  <button onClick={() => handleDownload(previewDoc)} data-testid="preview-download"
                    className="p-2 text-white/60 hover:text-white" title="Download">
                    <Download className="w-4 h-4" />
                  </button>
                  <button onClick={closePreview} data-testid="preview-close"
                    className="p-2 text-white/60 hover:text-white" title="Close">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <div className="flex-1 overflow-auto bg-[#050505]">
                {previewLoading ? (
                  <div className="flex items-center justify-center h-full">
                    <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : previewUrl ? (
                  isImage(previewDoc.content_type) ? (
                    <div className="flex items-center justify-center min-h-full p-4">
                      <img src={previewUrl} alt={previewDoc.original_filename}
                        style={{ width: `${previewZoom}%`, maxWidth: "none" }}
                        className="object-contain transition-all" />
                    </div>
                  ) : isPdf(previewDoc.content_type) ? (
                    <iframe src={previewUrl} title="PDF" className="w-full h-full bg-white" />
                  ) : (
                    <p className="text-white/60 text-center p-12">Preview unavailable for this file type</p>
                  )
                ) : null}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* ===== Asset Verification Modal ===== */}
      <AssetVerificationModal
        open={showVerification}
        extractedData={verificationData}
        documentName={verificationDocName}
        onClose={() => { setShowVerification(false); setVerificationData(null); }}
        onSuccess={() => { setShowVerification(false); setVerificationData(null); fetchTree(); }}
      />
    </div>
  );
}

function FolderRow({ testid, label, count, active, onClick, icon, flex, small }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`${flex ? "flex-1" : "w-full"} text-left flex items-center justify-between px-2.5 py-1.5 transition-colors ${
        active ? "bg-[#D4AF37]/10 text-[#D4AF37]" : "text-white/90 hover:bg-white/[0.03] hover:text-white"
      }`}
    >
      <span className={`flex items-center gap-2 ${small ? "text-[11px]" : "text-xs"} min-w-0 truncate`}>
        {icon}
        <span className="truncate">{label}</span>
      </span>
      <span className={`font-mono ${small ? "text-[9px]" : "text-[10px]"} ${active ? "text-[#D4AF37]/70" : "text-white/65"} flex-shrink-0 ml-2`}>{count}</span>
    </button>
  );
}
